import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Mail, MessageCircle, MapPin, Clock, ArrowLeft, ArrowRight, Send, QrCode, X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import MarketingLayout from "../components/MarketingLayout";
import { api, formatApiError } from "../lib/api";
import { brand } from "../context/CompanyContext";

/**
 * Public "Contacte-nos" page — multiple direct channels (WhatsApp, two
 * phone lines, email) plus a fall-back message form that posts to the
 * existing /api/contact endpoint used by the homepage form.
 *
 * v170: scannable QR codes for WhatsApp + Email so desktop visitors can
 * jump to their phone instantly without typing the number.
 *
 * Design follows the Dark Luxe palette to stay consistent with the rest
 * of the public site.
 */

const PHONES = [
  { display: "+351 936 942 709", tel: "+351936942709" },
  { display: "+351 936 942 629", tel: "+351936942629" },
];
const EMAIL = "luxstaymadeira@gmail.com";
// WhatsApp uses a dedicated WhatsApp Business line, separate from the
// telephone numbers above. The QR (image) and link both point here.
const WHATSAPP_PHONE = "351967386076";
const WHATSAPP_MSG = `Olá ${brand()}, gostaria de saber mais sobre os vossos serviços.`;
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(WHATSAPP_MSG)}`;
const MAILTO_URL = `mailto:${EMAIL}?subject=Pedido%20de%20Or%C3%A7amento%20%C2%B7%20${encodeURIComponent(brand())}`;
// Official WhatsApp Business QR (brand-approved, contains LuxStay logo).
// Sent by the owner — used in place of the auto-generated QR for the
// WhatsApp channel only. Email QR stays auto-generated.
const WHATSAPP_QR_IMG = "/assets/whatsapp-qr-luxstay.jpg";

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { title, value, label }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Por favor, preencha nome, email e mensagem.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Mensagem enviada. Respondemos em até 24h.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Erro ao enviar — tente WhatsApp ou ligar."));
    } finally {
      setSending(false);
    }
  }

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden bg-[#041f1b] py-16 sm:py-24"
               data-testid="contacto-hero">
        <div aria-hidden
             className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"
                data-testid="contacto-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
            <ArrowLeft size={14} /> Voltar à Home
          </Link>
          <div className="mt-8 max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              Estamos cá para si
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#fdfbf7]">
              Contacte-<span className="italic text-[#d4af37]">nos</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#a0b2af] leading-relaxed">
              Fale connosco a qualquer momento. Resposta garantida em menos de 24 horas
              úteis. Para urgências, prefira WhatsApp ou telefone.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#041f1b] pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Channels column */}
          <div className="space-y-4" data-testid="contacto-channels">
            {/* WhatsApp — most prominent · scannable QR + tap-to-open */}
            <div className="rounded-2xl border-2 border-[#25D366]/40 bg-[#25D366]/5 hover:border-[#25D366] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#25D366]/20 transition-all duration-300 p-5"
                 data-testid="contacto-whatsapp-card">
              <div className="flex items-start gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-[#25D366] text-white shrink-0">
                  <MessageCircle size={22} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#25D366]">WhatsApp · Resposta rápida</div>
                  <div className="font-display text-lg text-[#fdfbf7] mt-0.5">Falar agora no WhatsApp</div>
                  <div className="text-xs text-[#a0b2af] mt-1">+351 967 386 076 · Mensagem prévia preenchida</div>
                </div>
                {/* Inline mini-QR — visible on >=sm screens. Uses the
                    official WhatsApp Business QR image (with LuxStay logo). */}
                <button onClick={() => setQrModal({
                          title: `WhatsApp · ${brand()}`,
                          img: WHATSAPP_QR_IMG,
                          label: "Aponte a câmara para iniciar conversa",
                        })}
                        data-testid="contacto-whatsapp-qr-btn"
                        title="Ampliar QR Code"
                        aria-label="Ampliar QR Code WhatsApp"
                        className="hidden sm:grid place-items-center w-20 h-20 rounded-lg bg-white p-1 shrink-0 hover:scale-105 transition-transform overflow-hidden">
                  <img src={WHATSAPP_QR_IMG} alt={`QR Code WhatsApp ${brand()}`}
                       className="w-full h-full object-contain" loading="lazy" />
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                   data-testid="contacto-whatsapp"
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#25D366] text-white font-semibold uppercase tracking-wider text-xs hover:bg-[#1ebe5a] transition-colors">
                  <MessageCircle size={14} /> Abrir conversa <ArrowRight size={14} />
                </a>
                <button onClick={() => setQrModal({
                          title: `WhatsApp · ${brand()}`,
                          img: WHATSAPP_QR_IMG,
                          label: "Aponte a câmara para iniciar conversa",
                        })}
                        data-testid="contacto-whatsapp-qr-mobile"
                        className="sm:hidden inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#25D366]/60 text-[#25D366] font-semibold uppercase tracking-wider text-xs hover:bg-[#25D366]/10 transition-colors">
                  <QrCode size={14} /> Ver QR
                </button>
              </div>
            </div>

            {/* Phone lines */}
            {PHONES.map((p, i) => (
              <a key={p.tel}
                 href={`tel:${p.tel}`}
                 data-testid={`contacto-phone-${i}`}
                 className="group flex items-center gap-4 rounded-2xl border border-[#1a4640] bg-[#062c27] hover:border-[#d4af37]/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#d4af37]/10 transition-all duration-300 p-5">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
                  <Phone size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">
                    {i === 0 ? "Linha principal" : "Linha alternativa"}
                  </div>
                  <div className="font-display text-lg text-[#fdfbf7] mt-0.5 tracking-wide">{p.display}</div>
                  <div className="text-xs text-[#a0b2af] mt-1 inline-flex items-center gap-1.5">
                    <Clock size={11} /> Seg–Sáb · 07:00–19:00
                  </div>
                </div>
                <ArrowRight size={16} className="text-[#a0b2af] shrink-0 group-hover:text-[#d4af37] group-hover:translate-x-1 transition-all" />
              </a>
            ))}

            {/* Email · with inline scannable QR */}
            <div className="rounded-2xl border border-[#1a4640] bg-[#062c27] hover:border-[#d4af37]/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#d4af37]/10 transition-all duration-300 p-5"
                 data-testid="contacto-email-card">
              <div className="flex items-start gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
                  <Mail size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">Email</div>
                  <div className="font-display text-lg text-[#fdfbf7] mt-0.5 truncate">{EMAIL}</div>
                  <div className="text-xs text-[#a0b2af] mt-1">Resposta em até 24h úteis</div>
                </div>
                <button onClick={() => setQrModal({
                          title: `Email · ${brand()}`,
                          value: MAILTO_URL,
                          label: "Aponte a câmara para abrir email",
                        })}
                        data-testid="contacto-email-qr-btn"
                        title="Ampliar QR Code"
                        aria-label="Ampliar QR Code Email"
                        className="hidden sm:grid place-items-center w-20 h-20 rounded-lg bg-white p-1.5 shrink-0 hover:scale-105 transition-transform">
                  <QRCodeSVG value={MAILTO_URL} size={68} bgColor="#ffffff" fgColor="#041f1b" level="M" />
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a href={MAILTO_URL}
                   data-testid="contacto-email"
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#d4af37] bg-[#d4af37] text-[#041f1b] font-semibold uppercase tracking-wider text-xs hover:bg-[#e8c860] transition-colors">
                  <Mail size={14} /> Enviar email <ArrowRight size={14} />
                </a>
                <button onClick={() => setQrModal({
                          title: `Email · ${brand()}`,
                          value: MAILTO_URL,
                          label: "Aponte a câmara para abrir email",
                        })}
                        data-testid="contacto-email-qr-mobile"
                        className="sm:hidden inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#d4af37]/60 text-[#d4af37] font-semibold uppercase tracking-wider text-xs hover:bg-[#d4af37]/10 transition-colors">
                  <QrCode size={14} /> Ver QR
                </button>
              </div>
            </div>

            {/* Location strip */}
            <div className="flex items-center gap-4 rounded-2xl border border-dashed border-[#1a4640] bg-[#062c27]/50 p-5">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-[#1a4640] text-[#a0b2af] shrink-0">
                <MapPin size={20} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#a0b2af]">Onde estamos</div>
                <div className="text-sm text-[#fdfbf7] mt-0.5">Ponta do Sol &amp; Funchal · Madeira</div>
                <div className="text-xs text-[#a0b2af] mt-1">Servimos toda a ilha (Funchal, Caniço, Ponta do Sol, Calheta…)</div>
              </div>
            </div>
          </div>

          {/* Form column */}
          <form onSubmit={submit}
                data-testid="contacto-form"
                className="rounded-2xl border border-[#1a4640] bg-[#062c27] p-6 sm:p-8 h-fit">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">Prefere escrever?</div>
            <h2 className="mt-2 font-display text-2xl text-[#fdfbf7]">
              Deixe-nos uma mensagem
            </h2>
            <p className="mt-2 text-sm text-[#a0b2af]">
              Responderemos por email com o orçamento ou esclarecimentos.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Nome *" testId="contacto-name"
                         value={form.name} onChange={v => set("name", v)} />
              <FormField label="Telefone" testId="contacto-phone-input" type="tel"
                         value={form.phone} onChange={v => set("phone", v)} />
              <FormField label="Email *" testId="contacto-email-input" type="email" full
                         value={form.email} onChange={v => set("email", v)} />
              <label className="block sm:col-span-2">
                <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">Mensagem *</span>
                <textarea data-testid="contacto-message"
                          value={form.message}
                          onChange={e => set("message", e.target.value)}
                          rows={5}
                          placeholder="Conte-nos brevemente sobre a sua propriedade e necessidades…"
                          className="w-full px-3 py-2.5 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-[#d4af37] focus:outline-none transition-colors" />
              </label>
            </div>

            <button type="submit"
                    disabled={sending || sent}
                    data-testid="contacto-submit"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold uppercase tracking-widest text-xs bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-50 disabled:hover:translate-y-0">
              <Send size={14} />
              {sent ? "Mensagem enviada ✓" : sending ? "A enviar…" : "Enviar mensagem"}
            </button>
            <p className="mt-3 text-[11px] text-center text-[#a0b2af]/70">
              Os seus dados são usados apenas para responder ao seu pedido.
            </p>
          </form>
        </div>
      </section>

      {/* Fullscreen QR modal — desktop visitors scan to jump on phone */}
      {qrModal && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setQrModal(null)}
             data-testid="contacto-qr-modal">
          <div className="relative bg-[#062c27] border border-[#d4af37]/40 rounded-2xl p-8 sm:p-10 max-w-sm w-full text-center"
               onClick={e => e.stopPropagation()}>
            <button onClick={() => setQrModal(null)}
                    aria-label="Fechar"
                    data-testid="contacto-qr-close"
                    className="absolute top-3 right-3 p-2 rounded-full text-[#a0b2af] hover:text-[#fdfbf7] hover:bg-[#1a4640]/50 transition-colors">
              <X size={18} />
            </button>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">QR Code</div>
            <h3 className="mt-2 font-display text-xl text-[#fdfbf7]">{qrModal.title}</h3>
            <div className="mt-6 inline-block bg-white p-4 rounded-xl">
              {qrModal.img ? (
                <img src={qrModal.img} alt={qrModal.title}
                     className="w-[280px] h-[280px] object-contain" />
              ) : (
                <QRCodeSVG value={qrModal.value} size={240} bgColor="#ffffff" fgColor="#041f1b" level="M" />
              )}
            </div>
            <p className="mt-5 text-sm text-[#a0b2af]">{qrModal.label}</p>
          </div>
        </div>
      )}
    </MarketingLayout>
  );
}

function FormField({ label, value, onChange, type = "text", testId, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">{label}</span>
      <input data-testid={testId}
             type={type}
             value={value}
             onChange={e => onChange(e.target.value)}
             className="w-full px-3 py-2.5 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none transition-colors" />
    </label>
  );
}
